import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, AlertTriangle, Sparkles } from 'lucide-react';
import { api, type ProjectSummary } from '../api/client';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { EmptyState } from '../components/ui/EmptyState';
import { SiteCard } from '../components/SiteCard';
import { Skeleton } from '../components/ui/Skeleton';

interface Props {
  onOpen: (projectId: string) => void;
  onCreate: () => void;
}

export function ProjectsScreen({ onOpen, onCreate }: Props) {
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['projects'],
    queryFn: api.listProjects,
    refetchInterval: 5000,
  });

  const [filter, setFilter] = useState('');
  const projects = data?.projects ?? [];

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.brandId.toLowerCase().includes(q) ||
        (p.displayName ?? '').toLowerCase().includes(q) ||
        p.kitId.toLowerCase().includes(q),
    );
  }, [projects, filter]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight2 text-zinc-900">
            Your sites
          </h1>
          <p className="text-[13.5px] text-zinc-500 mt-1">
            {projects.length === 0
              ? 'Spin up a new SEO-ready website in about 20 minutes.'
              : `${projects.length} site${projects.length === 1 ? '' : 's'} · live updates every few seconds`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {projects.length > 0 && (
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter sites…"
                className="pl-8 h-9 text-[13px]"
              />
            </div>
          )}
          <Button onClick={onCreate} leftIcon={<Plus className="h-3.5 w-3.5" />}>
            Create site
          </Button>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-[13px] text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Couldn't load sites</div>
            <div className="text-[12.5px] text-red-700/90 mt-0.5">{String(error)}</div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="bg-white border border-zinc-200 rounded-xl p-5 flex flex-col gap-3 min-h-[160px]"
            >
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-12" />
              </div>
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-3 w-24 mt-auto" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && data && projects.length === 0 && (
        <EmptyState
          icon={<Sparkles className="h-5 w-5" />}
          title="No sites yet"
          description="Start by creating your first site. Paste a brand JSON and we'll handle research, content, images and the live preview — usually about 20 minutes for the first build."
          action={
            <Button onClick={onCreate} leftIcon={<Plus className="h-3.5 w-3.5" />}>
              Create your first site
            </Button>
          }
        />
      )}

      {!isLoading && data && projects.length > 0 && filtered.length === 0 && (
        <EmptyState
          title="No sites match your filter"
          description="Try a different search term."
        />
      )}

      {filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 animate-fade-in-up">
          {filtered.map((p: ProjectSummary) => (
            <SiteCard key={p.id} project={p} onOpen={() => onOpen(p.id)} />
          ))}
        </div>
      )}

      {!isLoading && isFetching && projects.length > 0 && (
        <div className="text-[11px] text-zinc-400 text-center mt-2 flex items-center justify-center gap-1.5">
          <span className="h-1 w-1 rounded-full bg-emerald-500 animate-pulse-dot" />
          Live
        </div>
      )}
    </div>
  );
}
